import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import everfieldLogo from '../assets/Everfield-Group.png';
import { smoothScrollTo } from '../utils/smoothScroll';
import './Navbar.css';

interface NavLink {
  label: string;
  href: string;
}

const NAV_LINKS: readonly NavLink[] = [
  { label: 'About', href: '#about' },
  { label: 'For Landowners', href: '#for-landowners' },
];

const SERVICES: readonly NavLink[] = [
  { label: 'Everfield Energy', href: '/everfield-energy' },
  { label: 'Everfield DC', href: '/everfield-dc' },
];

const SCROLL_THRESHOLD_VH = 0.25;

export default function Navbar(): React.JSX.Element {
  const [visible, setVisible] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const handleNavAnchor = useCallback((hash: string) => {
    if (isHome) {
      smoothScrollTo(hash.slice(1));
    } else {
      navigate('/' + hash);
    }
  }, [isHome, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const threshold = window.innerHeight * SCROLL_THRESHOLD_VH;
      setScrollProgress(Math.min(currentY / threshold, 1));
      setVisible(currentY < lastScrollY.current || currentY < 60);
      if (currentY > lastScrollY.current && currentY > 0) setMenuOpen(false);
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape or after scrolling down 100px
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    let accumulated = 0;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY > 0) accumulated += e.deltaY;
      if (accumulated >= 100) setMenuOpen(false);
    };
    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => { touchStartY = e.touches[0].clientY; };
    const onTouchMove = (e: TouchEvent) => {
      const delta = touchStartY - e.touches[0].clientY;
      if (delta >= 100) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((o) => !o), []);

  const bgAlpha = menuOpen ? 0.97 : scrollProgress * 0.85;
  const blurAmount = menuOpen ? 10 : scrollProgress * 10;

  return (
    <>
      <nav
        className="navbar"
        aria-label="Main navigation"
        style={{
          transform: visible ? 'translateY(0px)' : 'translateY(-100%)',
          backgroundColor: `rgba(15, 20, 15, ${bgAlpha})`,
          backdropFilter: `blur(${blurAmount}px)`,
          WebkitBackdropFilter: `blur(${blurAmount}px)`,
          boxShadow:
            scrollProgress > 0.5 ? `0 2px 20px rgba(0,0,0,${scrollProgress * 0.3})` : 'none',
        }}
      >
        <Link to="/" className="navbar__logo">
          <img src={everfieldLogo} alt="Everfield" width={370} height={49} fetchPriority="high" />
        </Link>
        <ul className="navbar__links">
          {NAV_LINKS.map(({ label, href }) => (
            <li key={label}>
              <a href={href} onClick={(e) => { e.preventDefault(); handleNavAnchor(href); }}>
                {label}
              </a>
            </li>
          ))}
          <li className="navbar__dropdown-wrap">
            <button
              className="navbar__dropdown-trigger"
              type="button"
              aria-haspopup="true"
              aria-expanded={servicesOpen}
              onClick={() => setServicesOpen((o) => !o)}
            >
              Services
              <svg
                className={`navbar__chevron${servicesOpen ? ' navbar__chevron--open' : ''}`}
                width="10" height="6" viewBox="0 0 10 6" fill="none"
                aria-hidden="true"
              >
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {servicesOpen && (
              <ul className="navbar__dropdown">
                {SERVICES.map(({ label, href }) => (
                  <li key={label}>
                    <Link to={href} onClick={() => setServicesOpen(false)}>{label}</Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>
        <button
          className={`navbar__hamburger${menuOpen ? ' navbar__hamburger--open' : ''}`}
          type="button"
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="navbar-mobile-menu"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </nav>

      <div
        id="navbar-mobile-menu"
        className={`navbar__mobile-menu${menuOpen ? ' navbar__mobile-menu--open' : ''}`}
        aria-hidden={!menuOpen}
      >
        {NAV_LINKS.map(({ label, href }) => (
          <a
            key={label}
            href={href}
            onClick={(e) => { e.preventDefault(); handleNavAnchor(href); closeMenu(); }}
            tabIndex={menuOpen ? 0 : -1}
          >
            {label}
          </a>
        ))}
        {SERVICES.map(({ label, href }) => (
          <Link
            key={label}
            to={href}
            onClick={closeMenu}
            tabIndex={menuOpen ? 0 : -1}
          >
            {label}
          </Link>
        ))}
      </div>
    </>
  );
}
